import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Progress } from '@heroui/progress';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { orderService } from '../../services/orderService';
import { ticketService } from '../../services/ticketService';
import { AccountDashboardStats } from '../../types/admin';

const Dashboard: React.FC = () => {
  const pageTitle = 'Account Dashboard';
  const pageDescription = 'Overview of your account activity and orders.';
  const { user } = useAuth();
  const { formatCurrency, convertPrice } = useLocale();
  const [stats, setStats] = useState<AccountDashboardStats>({
    totalOrders: 0,
    activeOrders: 0,
    totalSpent: 0,
    loyaltyPoints: 0,
    addressCount: 0,
    ticketCount: 0,
    recentOrders: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      // Fetch dashboard stats (you might need to create this API endpoint)
      const [ordersResponse, ticketsResponse] = await Promise.allSettled([
        orderService.getOrders({ page: 1, pageSize: 5 }),
        ticketService.getTickets({ page: 1, pageSize: 5 }),
      ]);

      if (ordersResponse.status === 'fulfilled') {
        const orders = ordersResponse.value.orders || [];
        const activeOrders = orders.filter((order: any) =>
          ['pending', 'processing', 'printing', 'printed', 'shipped'].includes(
            order.status.toLowerCase()
          )
        );

        setStats(prev => ({
          ...prev,
          totalOrders: ordersResponse.value.totalCount || 0,
          activeOrders: activeOrders.length,
          recentOrders: orders.slice(0, 3),
          totalSpent: orders.reduce(
            (sum: number, order: any) => sum + (order.totalAmount || 0),
            0
          ),
        }));
      }

      // Address count no longer used as addresses are managed via Stripe
      setStats(prev => ({ ...prev, addressCount: 0 }));

      if (ticketsResponse.status === 'fulfilled') {
        setStats(prev => ({
          ...prev,
          ticketCount: ticketsResponse.value.totalCount || 0,
        }));
      }
    } catch (error: any) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const getProfileCompletion = () => {
    let completed = 0;
    const total = 5;

    if (user?.firstName) completed++;
    if (user?.lastName) completed++;
    if (user?.email) completed++;
    if (user?.phoneNumber) completed++;
    if (user?.emailConfirmed) completed++;

    return Math.round((completed / total) * 100);
  };

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  const getOrderStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'printing':
        return 'secondary';
      case 'printed':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  const quickActions = [
    {
      title: 'View Orders',
      description: 'Track your recent purchases',
      icon: ShoppingBagIcon,
      href: '/account/orders',
      color: 'primary' as const,
    },
    // Addresses action removed
    {
      title: 'Support Center',
      description: 'Get help with your orders',
      icon: ChatBubbleLeftRightIcon,
      href: '/account/tickets',
      color: 'success' as const,
    },
  ];

  if (isLoading) {
    return (
      <div className='space-y-6 animate-pulse'>
        <div className='h-32 bg-gray-200 rounded-lg' />
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div className='h-24 bg-gray-200 rounded-lg' />
          <div className='h-24 bg-gray-200 rounded-lg' />
          <div className='h-24 bg-gray-200 rounded-lg' />
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='space-y-6'>
        {/* Welcome Section */}
        <Card>
          <CardBody className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <h2 className='text-2xl font-bold text-gray-900'>
                  Welcome back, {user?.firstName}! 👋
                </h2>
                <p className='text-gray-600 mt-1'>
                  Here's what's happening with your account
                </p>
              </div>
              <div className='hidden md:block'>
                <div className='text-right'>
                  <p className='text-sm text-gray-500'>Profile Completion</p>
                  <Progress
                    value={getProfileCompletion()}
                    className='mt-2 w-32'
                    color='primary'
                    size='sm'
                  />
                  <p className='text-xs text-gray-400 mt-1'>
                    {getProfileCompletion()}% complete
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Stats Overview */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          <Card>
            <CardBody className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-600'>Total Orders</p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {stats.totalOrders}
                  </p>
                </div>
                <div className='p-3 bg-primary-100 rounded-lg'>
                  <ShoppingBagIcon className='w-6 h-6 text-primary-600' />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-600'>Active Orders</p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {stats.activeOrders}
                  </p>
                </div>
                <div className='p-3 bg-warning-100 rounded-lg'>
                  <ClockIcon className='w-6 h-6 text-warning-600' />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-600'>Total Spent</p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {formatAmount(stats.totalSpent)}
                  </p>
                </div>
                <div className='p-3 bg-success-100 rounded-lg'>
                  <CurrencyDollarIcon className='w-6 h-6 text-success-600' />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Addresses stat removed */}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <h3 className='text-lg font-semibold'>Quick Actions</h3>
          </CardHeader>
          <CardBody>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {quickActions.map(action => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.title}
                    to={action.href}
                    className='p-4 rounded-lg border-2 border-transparent hover:border-primary-200 transition-all hover:shadow-md group'
                  >
                    <div className='flex items-center space-x-3'>
                      <div
                        className={`p-2 bg-${action.color}-100 rounded-lg group-hover:bg-${action.color}-200 transition-colors`}
                      >
                        <Icon className={`w-5 h-5 text-${action.color}-600`} />
                      </div>
                      <div className='flex-1'>
                        <h4 className='font-medium text-gray-900 group-hover:text-primary-600 transition-colors'>
                          {action.title}
                        </h4>
                        <p className='text-sm text-gray-500'>
                          {action.description}
                        </p>
                      </div>
                      <ArrowRightIcon className='w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors' />
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardBody>
        </Card>

        {/* Recent Orders */}
        {stats.recentOrders.length > 0 && (
          <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
              <div>
                <h3 className='text-lg font-semibold'>Recent Orders</h3>
                <p className='text-sm text-gray-600'>Your latest purchases</p>
              </div>
              <Button
                as={Link}
                to='/account/orders'
                variant='bordered'
                size='sm'
                endContent={<ArrowRightIcon className='w-4 h-4' />}
              >
                View All
              </Button>
            </CardHeader>
            <CardBody>
              <div className='space-y-4'>
                {stats.recentOrders.map((order: any) => (
                  <div
                    key={order.id}
                    className='flex items-center justify-between p-4 bg-gray-50 rounded-lg'
                  >
                    <div className='flex items-center space-x-3'>
                      <div className='p-2 bg-white rounded-lg'>
                        <TruckIcon className='w-5 h-5 text-gray-600' />
                      </div>
                      <div>
                        <p className='font-medium text-gray-900'>
                          Order #{order.orderNumber}
                        </p>
                        <p className='text-sm text-gray-500'>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center space-x-3'>
                      <div className='text-right'>
                        <p className='font-medium text-gray-900'>
                          {formatAmount(order.totalAmount || 0)}
                        </p>
                        <Chip
                          size='sm'
                          color={getOrderStatusColor(order.status)}
                          variant='flat'
                        >
                          {order.status}
                        </Chip>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Account Alerts */}
        {!user?.emailConfirmed && (
          <Card>
            <CardBody>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-3'>
                  <ExclamationTriangleIcon className='w-6 h-6 text-warning-500' />
                  <div>
                    <h4 className='font-medium text-gray-900'>
                      Email Verification Required
                    </h4>
                    <p className='text-sm text-gray-600'>
                      Please verify your email address to unlock all features
                    </p>
                  </div>
                </div>
                <Button
                  as={Link}
                  to='/account/profile'
                  color='warning'
                  variant='bordered'
                  size='sm'
                >
                  Verify Email
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
};

export default Dashboard;
